import { getInitials, getPersonaInitialsColor, Persona, PersonaCoin, PersonaPresence, PersonaSize } from '@fluentui/react';
import { PodProvider } from '../../api/pods.types';
import styles from './PodPersona.module.scss';
import { getProviderPosition } from './Util';

interface ProviderShape extends Pick<PodProvider, 'firstName' | 'lastName' | 'suffix' | 'avatarUrl'>, Partial<Pick<PodProvider, 'position'>> {}
interface Props {
  provider?: ProviderShape,
  text?: string,
  secondaryText?: string,
  noCoin?: boolean,
  size?: PersonaSize,
  inactive?: boolean,
  className?: string,
}

/**
 * A variant of Fluent UI's Persona component which accepts either a PodProvider
 * OR `text` and `secondaryText` values. If a provider is specified, the component
 * will use the provider's name as the primary text and their position as the secondary text.
 */
const PodPersona = ({provider, text, secondaryText, noCoin, size, inactive, className}: Props) => {
  const primary = provider
    ? `${provider.firstName} ${provider.lastName}${provider.suffix ? `, ${provider.suffix}`: ''}`
    : text!;

  /**
   * If provider is specified, obtain or infer their position (see below).
   * The reason we don't use `provider.position` directly is in order to
   * help surface when a provider is incorrectly assigned after a suffix change.
   * Although it's rare for a provider's suffix to change, it's not impossible.
   * Thus, it's conceivable that a provider could be, e.g., a Registered Nurse
   * in a pod they're assigned to while having an LVN suffix. Using getProviderPosition
   * ensures that if this happens, the user will see both the up-to-date suffix (here)
   * as well as the pod-specific position (in the Position column). Per spec, this
   * situation isn't an error state, but having this discontinuity should nudge astute
   * users into investigating.
   */
  const providerPosition = provider
    ? getProviderPosition(provider)
    : undefined;

  /**
   * If secondaryText is provided, use it as the secondary text. Otherwise, attempt to
   * infer the provider's position based on their suffix.
   */
  const secondary = secondaryText === undefined
    ? providerPosition
    : secondaryText;

  /**
   * @todo v2: Show a gold <Icon name="WarningSolid"/> with a tooltip if the provider's
   * current suffix doesn't match their pod-assigned role. Currently names and suffixes are
   * cached in the database so this wouldn't do anything, but if this behavior is removed,
   * I think it's important to warn users that, if a provider's suffix does change, they're
   * likely no longer able to fulfill their duties in the pod due to a change in role.
   */
  return (
    <Persona
      text={primary}
      secondaryText={secondary}
      imageUrl={provider?.avatarUrl ?? undefined}
      showSecondaryText={true}
      size={size ?? PersonaSize.size32}
      presence={PersonaPresence.none}
      className={`${className ? className : ''}${noCoin ? ` ${styles.noCoin}` : ''}`}
      onRenderPrimaryText={(props, defaultRender) => {
        if (inactive) return <span className={styles.inactive}>{primary}</span>;
        return defaultRender!(props);
      }}
      onRenderSecondaryText={!inactive ? undefined: (props) => (
        <span className={styles.inactive}>{props?.secondaryText}</span>
      )}
      onRenderPersonaCoin={(props) => {
        if (noCoin === true || !props) return null;

        const name = primary.split(",")?.[0];
        return <PersonaCoin
          className={inactive ? styles.inactive : ''}
          size={props.size}
          imageUrl={provider?.avatarUrl}
          imageInitials={getInitials(name, false)}
          imageShouldFadeIn={false}
          initialsColor={getPersonaInitialsColor({ text: name })}
        />
      }}
    />
  );
};

export default PodPersona;